import {Component, Input, OnInit} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {AccountAdminDto} from '../../dtos/accountAdmin.dto';
import {ReathletikAdminService} from '../../services/api/reathletik-admin.service';
import {ReathletikGiveAccessRequestDto} from '../../dtos/reathletikGiveAccessRequest.dto';
import {AccountAdminService} from '../../services/api/account-admin.service';
import {NotificationService} from '../../../../../common/src/lib/services/notification.service';
import {TextUtils} from '../../../../../common/src/lib/utils/TextUtils';
import {AccountRole} from 'common';
import {ClinikoGiveAccessRequestDto} from '../../dtos/clinikoGiveAccessRequest.dto';
import {ClinikoAdminService} from '../../services/api/cliniko-admin.service';
import {ModelHealthGiveAccessRequestDto} from '../../dtos/modelHealthGiveAccessRequest.dto';
import {ModelHealthAdminService} from '../../services/api/model-health-admin.service';
import {NookalPrepareIntegrationRequestDto} from '../../dtos/nookalPrepareIntegrationRequest.dto';
import {NookalAdminService} from '../../services/api/nookal-admin.service';
import {DialogConfirmComponent} from '../dialogs/dialog-confirm';
import {BaseComponent} from '../base.component';

@Component({
    selector: 'app-account-addons',
    templateUrl: './account-addons.component.html',
    styleUrls: ['./account-addons.component.scss']
})
export class AccountAddonsComponent extends BaseComponent implements OnInit {

    public REATHLETIK_ENROLL_DATE = 'reathletikEnrollDate';
    public REATHLETIK_S_START_ENROLL_DATE = 'reathletikSStartsEnrollDate';
    public REATHLETIK_TOKEN = 'reathletikToken';
    public REATHLETIK_S_START_TOKEN = 'reathletikSStartsToken';

    public CLINIKO_KINVENT_AUTHORIZED = 'kinventClinikoAuthorized';
    public CLINIKO_AUTHORIZED = 'clinikoAuthorized';

    public MODEL_HEALTH_KINVENT_AUTHORIZED = 'kinventModelHealthAuthorized';

    public NOOKAL_KINVENT_AUTHORIZED = 'kinventNookalAuthorized';
    public NOOKAL_AUTHORIZED = 'nookalAuthorized';

    @Input()
    public account: AccountAdminDto;

    constructor(
        private _reathletikAdminService: ReathletikAdminService,
        private _clinikoAdminService: ClinikoAdminService,
        private _modelHealthAdminService: ModelHealthAdminService,
        private _nookalAdminService: NookalAdminService,
        private _accountAdminService: AccountAdminService,
        private _notificationService: NotificationService,
        private _dialog: MatDialog
    ) {
        super();
    }

    ngOnInit(): void {}

    hasReathletik(): boolean {
        return this.hasMetaKey(this.REATHLETIK_TOKEN) || this.hasMetaKey(this.REATHLETIK_S_START_TOKEN);
    }

    hasCliniko(): boolean {
        return this.hasMetaKey(this.CLINIKO_AUTHORIZED) && this.hasMetaKey(this.CLINIKO_KINVENT_AUTHORIZED);
    }

    hasMetaKey(metaKey: string): boolean {
        return this.account.metaFields.find(meta => meta?.metaKey === metaKey) !== undefined;
    }

    getMetaValueDateTimestamp(metaKey: string): Date | undefined {
        const dateString = this.account.metaFields
            .find(meta => meta?.metaKey === metaKey)
            ?.metaValue;

        if (TextUtils.isEmpty(dateString)) {
            return undefined;
        }

        return new Date(parseFloat(dateString));
    }


    onProtocolGiveAccess(protocol: string): void {
        const giveAccessDto = new ReathletikGiveAccessRequestDto(this.account.code, [protocol]);
        this._reathletikAdminService.giveAccessToProtocols(giveAccessDto)
            .subscribe(() => {
                this.reloadAccount();
            }, error => {
                this._notificationService.info(`Give protocol access Failed: ${error.statusText} (${error.status})`);
                }
            );
    }

    hasModelHealth(): boolean {
        return this.hasMetaKey(this.MODEL_HEALTH_KINVENT_AUTHORIZED);
    }

    onModelHealthGiveAccess(): void {
        const giveAccessDto = new ModelHealthGiveAccessRequestDto(this.account.code);
        this._modelHealthAdminService.giveAccess(giveAccessDto)
            .subscribe(() => {
                    this.reloadAccount();
                }, error => {
                    this._notificationService.info(`Give Model Health access Failed: ${error.statusText} (${error.status})`);
                }
            );
    }

    onClinikoGiveAccess(): void {
        const giveAccessDto = new ClinikoGiveAccessRequestDto(this.account.code);
        this._clinikoAdminService.giveAccess(giveAccessDto)
            .subscribe(() => {
                    this.reloadAccount();
                }, error => {
                    this._notificationService.info(`Give Cliniko access Failed: ${error.statusText} (${error.status})`);
                }
            );
    }

    hasNookal(): boolean {
        return this.hasMetaKey(this.NOOKAL_AUTHORIZED) && this.hasMetaKey(this.NOOKAL_KINVENT_AUTHORIZED);
    }

    /**
     * Unlike the other integrations, the Nookal endpoint is restricted to admins on the server.
     */
    public get canApproveNookal(): boolean {
        return this.userAccount?.accountRoleType === AccountRole.ADMIN;
    }

    public nookalApproveTooltip(): string {
        if (!this.canApproveNookal) {
            return 'Only admins can approve the Nookal integration';
        }

        if (!this.hasMetaKey(this.NOOKAL_AUTHORIZED)) {
            return 'The customer must connect their Nookal account first';
        }

        return 'Approve the integration and import the participants from Nookal';
    }

    onNookalPrepareIntegration(): void {
        this._dialog.open(DialogConfirmComponent, {
            data: {
                title: 'Approve the Nookal integration?',
                warning: 'This also starts the first import of the participants from Nookal, which can take a while.',
                message: `The integration will be approved for ${this.account.email} and their Nookal participants `
                    + 'will be created in our system.',
                confirmText: 'Approve & import'
            }
        }).afterClosed().subscribe(confirmed => {
            if (!confirmed) {
                return;
            }

            const prepareIntegrationDto = new NookalPrepareIntegrationRequestDto(this.account.code);
            this._nookalAdminService.prepareIntegration(prepareIntegrationDto)
                .subscribe(() => {
                        this.reloadAccount();
                    }, error => {
                        this._notificationService.info(`Nookal integration Failed: ${error.statusText} (${error.status})`);
                    }
                );
        });
    }

    private reloadAccount(): void {
        this._accountAdminService.get(this.account.code)
            .subscribe(account => {
                this.account = account;
            }, error => {
                this._notificationService.info(`Get Account Failed: ${error.statusText} (${error.status})`);
            });
    }
}
